import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import styles from "../../../styles/pages/profile/ProfilePage.module.css";
import {
  FaStar,
  FaEdit,
  FaTrash,
  FaExclamationTriangle,
  FaClock,
  FaImage,
  FaFilter,
  FaSort,
} from "react-icons/fa";
import getImageUrl from "../../../utils/imageUtils";
import { formatDate } from "../../../utils/dateFormatter";
import api from "../../../utils/api/axiosInstance";
import ReviewsViewSkeleton from "./ReviewsViewSkeleton";
import ConfirmModal from "../../common/ConfirmModal";
import useBulkSelection from "../bulk/useBulkSelection";
import BulkSelectBar, {
  SelectCheckbox,
  SelectModeButton,
} from "../bulk/BulkSelectBar";
import bulkStyles from "../bulk/BulkSelectBar.module.css";
import runBulkRequests from "../../../utils/runBulkRequests";

const ReviewsView = ({
  reviews,
  loading,
  error,
  onRefresh,
  showToast,
  currentTab,
}) => {
  const [filterRating, setFilterRating] = useState(null); // null = all, 1-5 = specific rating
  const [sortBy, setSortBy] = useState("date"); // 'date' | 'rating'
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingIds, setPendingIds] = useState([]);

  // Filter and sort reviews
  const filteredAndSortedReviews = useMemo(() => {
    let result = [...reviews];

    // Filter by rating
    if (filterRating !== null) {
      result = result.filter((review) => review.rating === filterRating);
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === "date") {
        return new Date(b.createdAt) - new Date(a.createdAt);
      } else if (sortBy === "rating") {
        return b.rating - a.rating;
      }
      return 0;
    });

    return result;
  }, [reviews, filterRating, sortBy]);

  const visibleIds = useMemo(
    () => filteredAndSortedReviews.map((review) => review._id).filter(Boolean),
    [filteredAndSortedReviews],
  );
  const {
    selectMode,
    selectedIds,
    allVisibleSelected,
    toggle,
    selectAllVisible,
    exitSelectMode,
    toggleSelectMode,
    setSelectedIds,
  } = useBulkSelection(visibleIds);

  const openDelete = (ids) => {
    if (!ids.length || deleting) return;
    setPendingIds(ids);
    setShowDeleteModal(true);
  };

  const confirmDeleteReview = async () => {
    if (pendingIds.length === 0) return;
    const ids = pendingIds;

    try {
      setDeleting(true);
      setShowDeleteModal(false);
      const { succeeded, failed, failedIds } = await runBulkRequests(ids, (id) =>
        api.delete(`/products/reviews/${id}`),
      );
      if (succeeded > 0) {
        showToast({
          message:
            succeeded === 1
              ? "Review deleted successfully"
              : `${succeeded} reviews deleted`,
          type: "success",
        });
        onRefresh();
      }
      if (failed > 0) {
        showToast({
          message:
            succeeded === 0
              ? "Failed to delete review"
              : `${failed} reviews could not be deleted`,
          type: "error",
        });
        setSelectedIds(failedIds);
      } else if (selectMode) {
        exitSelectMode();
      }
    } catch (err) {
      console.error("Error deleting review:", err);
      showToast({
        message: err.response?.data?.message || "Failed to delete review",
        type: "error",
      });
    } finally {
      setDeleting(false);
      setPendingIds([]);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className={styles.starRating}>
        {[1, 2, 3, 4, 5].map((star) => (
          <FaStar
            key={star}
            className={star <= rating ? styles.starFilled : styles.starEmpty}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return <ReviewsViewSkeleton />;
  }

  if (error) {
    return (
      <div className={styles.contentSection}>
        <div className={styles.errorState}>
          <div className={styles.errorStateIcon}>
            <FaExclamationTriangle size={48} />
          </div>
          <h3>Unable to Load Reviews</h3>
          <p>{error}</p>
          <button className="buttonPrimary" onClick={onRefresh}>
            <FaClock /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.contentSection}>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>
            <FaStar className={styles.sectionIcon} />
            My Reviews
          </h2>
          {reviews.length > 0 && (
            <p className={styles.sectionSubtitle}>
              {filteredAndSortedReviews.length} review
              {filteredAndSortedReviews.length === 1 ? "" : "s"}
              {filterRating && ` · ${filterRating} stars`}
            </p>
          )}
        </div>
        {reviews.length > 0 && (
          <div className={styles.bulkActions}>
            <button
              className="buttonSecondary"
              onClick={onRefresh}
              disabled={loading}
              title="Refresh Reviews"
            >
              <FaClock /> Refresh
            </button>
            <SelectModeButton
              active={selectMode}
              onClick={toggleSelectMode}
              disabled={deleting}
            />
          </div>
        )}
      </div>

      {selectMode && (
        <BulkSelectBar
          selectedCount={selectedIds.length}
          allSelected={allVisibleSelected}
          hasHidden={reviews.length > filteredAndSortedReviews.length}
          busy={deleting}
          onSelectAll={selectAllVisible}
          onDelete={() => openDelete(selectedIds)}
          noun="reviews"
        />
      )}

      {reviews.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>
            <FaStar size={64} />
          </div>
          <h3>No Reviews Yet</h3>
          <p>
            You haven't written any product reviews yet. Share your experience
            with products to help others make informed decisions.
          </p>
          <Link to="/search" className="buttonPrimary">
            Browse Products
          </Link>
        </div>
      ) : (
        <>
          {/* Filters and Sort */}
          <div className={styles.filterBar}>
            <div className={styles.filterGroup}>
              <FaFilter className={styles.filterIcon} />
              <label>Filter by rating:</label>
              <select
                value={filterRating || "all"}
                onChange={(e) =>
                  setFilterRating(
                    e.target.value === "all" ? null : parseInt(e.target.value),
                  )
                }
                className={styles.filterSelect}
              >
                <option value="all">All Ratings</option>
                <option value="5">5 Stars</option>
                <option value="4">4 Stars</option>
                <option value="3">3 Stars</option>
                <option value="2">2 Stars</option>
                <option value="1">1 Star</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <FaSort className={styles.filterIcon} />
              <label>Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="date">Most Recent</option>
                <option value="rating">Highest Rating</option>
              </select>
            </div>
          </div>

          {/* Reviews Grid */}
          <div className={styles.reviewsGrid}>
            {filteredAndSortedReviews.map((review) => {
              const isSelected = selectedIds.includes(review._id);
              return (
              <div
                key={review._id}
                className={`${styles.reviewCard} ${
                  selectMode ? bulkStyles.selectable : ""
                } ${isSelected ? bulkStyles.cardSelected : ""}`}
                onClick={(event) => {
                  if (!selectMode) return;
                  if (event.target.closest("a, button, input, label")) return;
                  toggle(review._id);
                }}
              >
                <div className={styles.reviewCardHeader}>
                  <div className={styles.reviewProductInfo}>
                    {review.product?.images?.[0] ? (
                      <img
                        src={getImageUrl(review.product.images[0])}
                        alt={review.product.name}
                        className={styles.reviewProductImage}
                      />
                    ) : (
                      <div className={styles.reviewProductPlaceholder}>
                        <FaImage />
                      </div>
                    )}
                    <div className={styles.reviewProductDetails}>
                      <Link
                        to={`/product/${review.product?._id}`}
                        state={{ fromProfileTab: currentTab }}
                        className={styles.reviewProductName}
                      >
                        {review.product?.name || "Unknown Product"}
                      </Link>
                      {renderStars(review.rating)}
                      {review.approved === false && (
                        <span className={styles.pendingBadge}>Pending approval</span>
                      )}
                    </div>
                  </div>
                  <div className={styles.reviewCardMeta}>
                    <span className={styles.reviewDate}>
                      {formatDate(review.createdAt)}
                    </span>
                  </div>

                  <div className={styles.reviewActions}>
                    {selectMode ? (
                      <SelectCheckbox
                        checked={isSelected}
                        onChange={() => toggle(review._id)}
                        label={`Select review of ${review.product?.name || "product"}`}
                        disabled={deleting}
                      />
                    ) : (
                      <>
                        <Link
                          to={`/product/${review.product?._id}`}
                          state={{ fromProfileTab: currentTab }}
                          className={styles.reviewActionButton}
                          title="View Product"
                        >
                          View
                        </Link>
                        <Link
                          to={`/product/${review.product?._id}`}
                          state={{
                            fromProfileTab: currentTab,
                            editReviewId: review._id,
                          }}
                          className={styles.reviewActionButton}
                          title="Edit Review"
                        >
                          <FaEdit size={18} />
                        </Link>
                        <button
                          onClick={() => openDelete([review._id])}
                          className={`${styles.reviewActionButton} ${styles.reviewDeleteButton}`}
                          disabled={deleting}
                          title="Delete Review"
                        >
                          <FaTrash size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {review.title && (
                  <h4 className={styles.reviewTitle}>{review.title}</h4>
                )}

                <p className={styles.reviewText}>{review.comment}</p>

                {review.images && review.images.length > 0 && (
                  <div className={styles.reviewImages}>
                    {review.images.map((img, idx) => (
                      <img
                        key={idx}
                        src={getImageUrl(img)}
                        alt={`Review ${idx + 1}`}
                        className={styles.reviewImage}
                      />
                    ))}
                  </div>
                )}

                <div className={styles.reviewStats}>
                  {(review.helpful > 0 || review.helpfulCount > 0) && (
                    <span>
                      {review.helpful || review.helpfulCount} found this helpful
                    </span>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </>
      )}

      {showDeleteModal && (
        <ConfirmModal
          message={
            pendingIds.length > 1
              ? `Delete ${pendingIds.length} reviews?`
              : "Are you sure you want to delete this review?"
          }
          onConfirm={confirmDeleteReview}
          onClose={() => {
            if (deleting) return;
            setShowDeleteModal(false);
            setPendingIds([]);
          }}
        />
      )}
    </div>
  );
};

export default React.memo(ReviewsView);
